/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import Login from '@layouts/auth/login/Login';
import SamlAuth from '@layouts/auth/saml/SamlAuth';
import RecoverPassword from '@layouts/auth/recoverPassword/RecoverPassword';
import ResetPassword from '@layouts/auth/resetPasswordPage/ResetPassword';
import ProtectedRoute from './ProtectedRoute';

import { Navigation } from '@constants/Navigation';
import { setAuthConfiguration } from '@services/api/configuration';
import { GlobalSettings, User } from '@services/types';
import api from '@services/api';
import Main from '@layouts/main';
import time from '@services/time';
import { ApiUserColumnsResponse, AppPropsResponse, TagEnum } from '@openapi';
import ChangeExpiredPassword from '@layouts/auth/changeExpiredPassword/ChangeExpiredPassword';
import auth from '@services/api/auth';

interface Context {
  token: null | string;
  user: User | null;
  settings: GlobalSettings | null;
  setToken: (value: null | string) => void;
  setUser: (value: null | User) => void;
  updateGlobalSettings: (value: GlobalSettings) => void;
  url: string | null;
  setUrl: (value: null | string) => void;
  variablesProfile: AppPropsResponse | null;
  setVariablesProfile: (value: AppPropsResponse | null) => void;
  deviceFilter: string | null;
  setDeviceFilter: (value: null | string) => void;
  deviceTagsFilter: TagEnum | undefined | any | null;
  setDeviceTagsFilter: (value: TagEnum | undefined | any | null) => void;
  hideSideBar: boolean | null;
  setHideSideBar: (value: null | boolean) => void;
  userId: string | null | undefined;
  setUserId: (value: null | string) => void;
  columnsDevice: ApiUserColumnsResponse['columns'] | undefined;
  setColumnsDevice: (value: ApiUserColumnsResponse['columns'] | undefined) => void;
  columnsDeviceEtc: ApiUserColumnsResponse['etc'] | undefined;
  setColumnsDeviceEtc: (value: ApiUserColumnsResponse['etc'] | undefined) => void;
  columnsStb: ApiUserColumnsResponse['columns'] | undefined;
  setColumnsStb: (value: ApiUserColumnsResponse['columns'] | undefined) => void;
  columnsStbEtc: ApiUserColumnsResponse['etc'] | undefined;
  setColumnsStbEtc: (value: ApiUserColumnsResponse['etc'] | undefined) => void;
  columnsRules: ApiUserColumnsResponse['columns'] | undefined;
  setColumnsRules: (value: ApiUserColumnsResponse['columns'] | undefined) => void;
  columnsRulesEtc: ApiUserColumnsResponse['etc'] | undefined;
  setColumnsRulesEtc: (value: ApiUserColumnsResponse['etc'] | undefined) => void;
  userLoggedIn: boolean | null;
  setUserLoggedIn: (value: null | boolean) => void;
}

const storedToken = sessionStorage.getItem('token');

export const AppContext = React.createContext<Context>({
  token: storedToken,
  user: null,
  settings: null,
  setToken: null as any,
  setUser: null as any,
  updateGlobalSettings: null as any,
  url: null as any,
  setUrl: null as any,
  variablesProfile: null as any,
  setVariablesProfile: null as any,
  deviceFilter: null as any,
  setDeviceFilter: null as any,
  deviceTagsFilter: null as any,
  setDeviceTagsFilter: null as any,
  hideSideBar: null as any,
  setHideSideBar: null as any,
  userId: null as any,
  setUserId: null as any,
  userLoggedIn: null as any,
  setUserLoggedIn: null as any,
  columnsDevice: null as any,
  setColumnsDevice: null as any,
  columnsDeviceEtc: null as any,
  setColumnsDeviceEtc: null as any,
  columnsStb: null as any,
  setColumnsStb: null as any,
  columnsStbEtc: null as any,
  setColumnsStbEtc: null as any,
  columnsRules: null as any,
  setColumnsRules: null as any,
  columnsRulesEtc: null as any,
  setColumnsRulesEtc: null as any,
});

const Router = () => {
  const [token, setToken] = React.useState<null | string>(storedToken);
  const [settings, setSettings] = React.useState<null | GlobalSettings>(null);
  const [user, setUser] = React.useState<null | User>(null);
  const [url, setUrl] = React.useState<null | string>(null);
  const [variablesProfile, setVariablesProfile] = React.useState<AppPropsResponse | null>(null);
  const [deviceFilter, setDeviceFilter] = React.useState<null | string>(null);
  const [deviceTagsFilter, setDeviceTagsFilter] = React.useState<any | TagEnum | undefined | null>();
  const [columnsDevice, setColumnsDevice] = useState<ApiUserColumnsResponse['columns'] | undefined>();
  const [columnsDeviceEtc, setColumnsDeviceEtc] = useState<ApiUserColumnsResponse['etc'] | undefined>();
  const [columnsStb, setColumnsStb] = useState<ApiUserColumnsResponse['columns'] | undefined>();
  const [columnsStbEtc, setColumnsStbEtc] = useState<ApiUserColumnsResponse['etc'] | undefined>();
  const [columnsRules, setColumnsRules] = useState<ApiUserColumnsResponse['columns'] | undefined>();
  const [columnsRulesEtc, setColumnsRulesEtc] = useState<ApiUserColumnsResponse['etc'] | undefined>();
  const [hideSideBar, setHideSideBar] = React.useState<null | boolean>(false);
  const location = React.useRef<ReturnType<typeof useLocation>>();
  const [userId, setUserId] = React.useState<null | string | undefined>();
  const [userLoggedIn, setUserLoggedIn] = useState(() => {
    const storedIsLoggedIn = localStorage.getItem('userLoggedIn');
    return storedIsLoggedIn !== null ? JSON.parse(storedIsLoggedIn) : false;
  });
  location.current = useLocation();
  const history = useHistory();

  React.useEffect(() => {
    if (token) {
      api.auth
        .me()
        .then(setUser)
        .then(() => {
          if (
            (token || user) &&
            ['/login', '/forgot-password', '/saml', '/reset-confirm'].includes(location.current?.pathname || '')
          ) {
            history.push(Navigation.Main);
          }
        });
      api.administration.getGlobalSettings().then((newSettings) => {
        setSettings({
          ...newSettings,
          jobExportBatchWaitDuration: time.fromIso8601ToMs(newSettings.jobExportBatchWaitDuration),
          jobBatchWaitDuration: time.fromIso8601ToMs(newSettings.jobBatchWaitDuration),
        });
      });
      if (user) {
        Promise.all([
          api.administration.getColumnsDevice(user.id),
          api.administration.getColumnsStbGroup(user.id),
        ]).then(([deviceCol, groupCol]) => {
          setColumnsDevice(deviceCol.columns);
          setColumnsDeviceEtc(deviceCol.etc);
          setColumnsStb(groupCol?.columns);
          setColumnsStbEtc(groupCol?.etc);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id, settings?.enableTags]);

  const handleTokenExpiration = () => {
    toast.error('Your token has expired');
    setToken(null);
  };

  const handleTimeExpiration = async () => {
    try {
      history.push(Navigation.Login);
      await auth.logOut();
      toast.error('Your token has expired');
      setToken(null);
    } catch (err) {
      console.log(err);
    } finally {
      setUser(null);
    }
  };

  const regex = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;

  const parseISO8601DurationToMinutes = (isoDurationString: string) => {
    const matches = isoDurationString.match(regex);

    if (!matches) {
      throw new Error('Invalid ISO 8601 duration string');
    }

    const hours = parseInt(matches[2] || '0', 10);
    const minutes = parseInt(matches[3] || '0', 10);
    const seconds = parseInt(matches[4] || '0', 10);

    return hours * 60 + minutes + seconds / 60;
  };

  const tokenLife = variablesProfile?.tokenTtl || 'PT2H';
  const duration = parseISO8601DurationToMinutes(tokenLife);
  const inactivityTimeout = duration * 60 * 1000;

  useEffect(() => {
    if (userLoggedIn) {
      const timer = setTimeout(() => {
        handleTimeExpiration();
        setUserLoggedIn(false);
        localStorage.removeItem('userLoggedIn');
      }, inactivityTimeout);

      return () => clearTimeout(timer);
    }
  }, [userLoggedIn]);

  const updateGlobalSettings = React.useCallback(
    async (values: GlobalSettings) => {
      await api.administration.updateGlobalSettings({
        ...settings,
        ...values,
        jobExportBatchWaitDuration: time.toIso8601FromMs(values.jobExportBatchWaitDuration),
        jobBatchWaitDuration: time.toIso8601FromMs(values.jobBatchWaitDuration),
      });
      setSettings({ ...settings, ...values });
    },
    [settings],
  );

  if (token) {
    setAuthConfiguration({ token, handleTokenExpiration });
    sessionStorage.setItem('token', token);
  } else {
    setAuthConfiguration({ token: null });
    sessionStorage.removeItem('token');
  }
  React.useEffect(() => {
    api.administration.getSettings().then((data) => {
      setVariablesProfile(data);
      setUrl(data.profile);
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        token,
        setToken,
        user,
        setUser,
        settings,
        updateGlobalSettings,
        url,
        setUrl,
        deviceFilter,
        setDeviceFilter,
        deviceTagsFilter,
        setDeviceTagsFilter,
        hideSideBar,
        setHideSideBar,
        userId,
        setUserId,
        variablesProfile,
        setVariablesProfile,
        userLoggedIn,
        setUserLoggedIn,
        columnsDevice,
        setColumnsDevice,
        columnsDeviceEtc,
        setColumnsDeviceEtc,
        columnsStb,
        columnsStbEtc,
        setColumnsStb,
        setColumnsStbEtc,
        columnsRules,
        setColumnsRules,
        columnsRulesEtc,
        setColumnsRulesEtc,
      }}
    >
      <Switch>
        <Route exact path={Navigation.Login} component={Login} />
        <Route exact path={Navigation.ChangePassword} component={ChangeExpiredPassword} />
        <Route exact path={Navigation.Saml} component={SamlAuth} />
        <Route exact path={Navigation.RecoverPassword} component={RecoverPassword} />
        <Route exact path={Navigation.ResetPassword} component={ResetPassword} />
        <ProtectedRoute path={Navigation.Main} component={Main} />
        <Redirect to={Navigation.Login} />
      </Switch>
    </AppContext.Provider>
  );
};
export default React.memo(Router);
